import React, { MouseEventHandler, useCallback } from 'react';

import { useAtom } from 'jotai/index';

import {
  cookieJar,
  uiRoutes,
  useCashAdvanceData,
  usePaymentMethods,
} from '@consumer-frontend/nc-api';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Icon,
  ListGroupItem,
} from '@consumer-frontend/shared-ui';

import { showMobileNavAtom } from '../../store';
import { NcNavLink } from '../NcNavLink';

import '@consumer-bootstrap/brands/netcredit/nav.scss';
import '@consumer-bootstrap/netcredit.scss';

export type LocNavPropsT = {
  accountId: string;
  openLoanNumber: string;
  showFeeSaverStatus: boolean;
};

export type LocNavT = React.FC<LocNavPropsT>;

export const LocNav: LocNavT = ({
  accountId,
  openLoanNumber,
  showFeeSaverStatus,
}) => {
  const [, setShowMobileNav] = useAtom(showMobileNavAtom);

  const { currentBankAccount } = usePaymentMethods(accountId);
  const { cashAdvanceData } = useCashAdvanceData();

  const showRequestCashAdvance = !!currentBankAccount?.isValid && !!cashAdvanceData?.eligibleToDraw;

  const closeMobileNav: MouseEventHandler = useCallback(
    (ev) => {
      (ev?.currentTarget as HTMLElement)?.blur();

      setShowMobileNav(false);
    },
    [setShowMobileNav],
  );

  const handleSignout = useCallback(
    () => {
      cookieJar.expire();
      setShowMobileNav(false);
    },
    [setShowMobileNav],
  );

  return (
    <>
      <ListGroupItem>
        <NcNavLink to={uiRoutes.legacyAH.accountSummary} onClick={closeMobileNav}>
          <Icon iconName="house-door" />
          <span data-text="My Account">My Account</span>
        </NcNavLink>
      </ListGroupItem>
      <ListGroupItem>
        <NcNavLink to={uiRoutes.dashboard.makeAPayment} onClick={closeMobileNav}>
          <Icon iconName="currency-dollar" />
          <span>Make a Payment</span>
        </NcNavLink>
        {showRequestCashAdvance && (
          <NcNavLink to={uiRoutes.dashboard.requestCashAdvance} onClick={closeMobileNav}>
            <Icon iconName="cash" />
            <span>Request a Cash Advance</span>
          </NcNavLink>
        )}
        <NcNavLink to={uiRoutes.legacyAH.loanDetails} onClick={closeMobileNav}>
          <Icon iconName="calculator" />
          <span>Minimum Payment Estimator</span>
        </NcNavLink>
        {showFeeSaverStatus && (
          <NcNavLink to={uiRoutes.legacyAH.feeSaverStatus} onClick={closeMobileNav}>
            <Icon iconName="coin" />
            <span>Fee Saver Status</span>
          </NcNavLink>
        )}
      </ListGroupItem>
      <ListGroupItem>
        <NcNavLink to={uiRoutes.legacyAH.statements(openLoanNumber)} onClick={closeMobileNav}>
          <Icon iconName="file-ruled" />
          <span>View Statements</span>
        </NcNavLink>
        <NcNavLink to={uiRoutes.dashboard.contract} onClick={closeMobileNav}>
          <Icon iconName="file-earmark-text" />
          <span>View Contract</span>
        </NcNavLink>
      </ListGroupItem>
      <ListGroupItem>
        <Dropdown>
          <DropdownToggle as="button" className="nav-link">
            <Icon iconName="person" />
            <span>My Info</span>
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.legacyAH.personalInfo}
              onClick={closeMobileNav}
            >
              <span>Personal Info</span>
            </DropdownItem>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.legacyAH.bankInfo}
              onClick={closeMobileNav}
            >
              <span>Bank Info</span>
            </DropdownItem>
            <DropdownItem
              className="nav-link"
              to={uiRoutes.legacyAH.employmentInfo}
              onClick={closeMobileNav}
            >
              <span>Employment Info</span>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
        <NcNavLink to={uiRoutes.dashboard.messages} onClick={closeMobileNav}>
          <Icon iconName="envelope" />
          <span>Message Center</span>
        </NcNavLink>
        <NcNavLink to={uiRoutes.legacyAH.contactUs} onClick={closeMobileNav}>
          <Icon iconName="chat-left" />
          <span>Contact Us</span>
        </NcNavLink>
      </ListGroupItem>
      <ListGroupItem>
        <NcNavLink to={uiRoutes.legacyAH.logOut} onClick={handleSignout}>
          <Icon iconName="box-arrow-right" />
          <span>Sign Out</span>
        </NcNavLink>
      </ListGroupItem>
    </>
  );
};
